import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, ChevronDown, FileText } from 'lucide-react';
import { COLLECTION_NOTICE, VIP_THIRD_PARTY_NOTICE, type NoticeRow } from '../../lib/privacyNotice';
import { ConsentBox as Box } from '../ui/ConsentCheckbox';

// 회원가입 동의 칸(2026-10-27 대표 지시): 이용약관·개인정보 수집·이용·제3자 제공(VIP 검색자 알림)을 각각 받는다.
// 세 칸 모두 필수라 부모에는 "모두 체크했는지" 하나만 알린다. 고지 문장은 privacyNotice.ts 정본을 그대로 펼쳐 보인다.

type ItemKey = 'terms' | 'collection' | 'thirdParty';

interface Props {
    onChange: (allRequiredAgreed: boolean) => void;
    onOpenTerms: () => void;
    showWarning: boolean;
}

const ITEMS: { key: ItemKey; label: string; notice?: NoticeRow[] }[] = [
    { key: 'terms', label: '서비스 이용약관 동의' },
    { key: 'collection', label: '개인정보 수집·이용 동의', notice: COLLECTION_NOTICE },
    { key: 'thirdParty', label: '개인정보 제3자 제공 동의 (VIP 검색자 알림)', notice: VIP_THIRD_PARTY_NOTICE },
];

export const SignupConsentChecklist: React.FC<Props> = ({ onChange, onOpenTerms, showWarning }) => {
    const [checked, setChecked] = useState<Record<ItemKey, boolean>>({ terms: false, collection: false, thirdParty: false });
    const [open, setOpen] = useState<ItemKey | null>(null);
    const all = checked.terms && checked.collection && checked.thirdParty;

    useEffect(() => { onChange(all); }, [all, onChange]);

    const toggleAll = () => setChecked({ terms: !all, collection: !all, thirdParty: !all });
    const toggle = (key: ItemKey) => setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

    return (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2 }} className="relative">
            {/* 2026-11-03 새 디자인: 초록 유리 상자 → 한 단계 올라온 면(모서리 20) + 헤어라인. 글귀·동작은 그대로 */}
            <div className="relative rounded-[20px] border border-white/[0.08] bg-[#0F0F11] px-4 py-4 space-y-3.5">
                <label className="flex items-center gap-3 cursor-pointer group pb-3.5 border-b border-white/[0.07]">
                    <input type="checkbox" checked={all} onChange={toggleAll} className="sr-only" />
                    <Box checked={all} />
                    <span className="text-[15px] text-[#FAFAFA] font-semibold">전체 동의</span>
                </label>

                {ITEMS.map((item) => (
                    <div key={item.key}>
                        <div className="flex items-start gap-3">
                            <label className="flex items-start gap-3 cursor-pointer group flex-1 min-w-0">
                                <input type="checkbox" checked={checked[item.key]} onChange={() => toggle(item.key)} className="sr-only" />
                                <Box checked={checked[item.key]} />
                                <span className="text-[14px] text-[#D4D4D8] leading-relaxed break-keep">
                                    <span className="text-[#A1A1AA] font-semibold mr-1">[필수]</span>
                                    {item.label}
                                </span>
                            </label>
                            {item.key === 'terms' ? (
                                <button type="button" onClick={onOpenTerms} className="text-[12px] text-[#A1A1AA] hover:text-[#FAFAFA] underline underline-offset-4 inline-flex items-center gap-1 flex-shrink-0 mt-0.5 py-1">
                                    보기 <FileText className="w-3 h-3" />
                                </button>
                            ) : (
                                <button type="button" onClick={() => setOpen(open === item.key ? null : item.key)} className="text-[12px] text-[#A1A1AA] hover:text-[#FAFAFA] inline-flex items-center gap-0.5 flex-shrink-0 mt-0.5 py-1" aria-expanded={open === item.key}>
                                    내용 <ChevronDown className={`w-3 h-3 transition-transform ${open === item.key ? 'rotate-180' : ''}`} />
                                </button>
                            )}
                        </div>
                        <AnimatePresence>
                            {open === item.key && item.notice && (
                                <motion.dl initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-2 ml-8 sm:ml-9 rounded-xl bg-black/60 border border-white/[0.06] px-3 py-2.5 space-y-1.5 overflow-hidden">
                                    {item.notice.map((row) => (
                                        <div key={row.label}>
                                            <dt className="text-[11px] text-[#A1A1AA] font-semibold">{row.label}</dt>
                                            <dd className="text-[11px] text-[#71717A] leading-relaxed">{row.value}</dd>
                                        </div>
                                    ))}
                                </motion.dl>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </div>

            <AnimatePresence>
                {showWarning && !all && (
                    <motion.div initial={{ opacity: 0, y: -5, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} exit={{ opacity: 0, y: -5, height: 0 }} transition={{ duration: 0.3 }} className="mt-2">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="mt-[2px] w-4 h-4 text-[#FBBF24] flex-shrink-0" />
                            <p className="text-[13px] leading-relaxed text-[#FBBF24] break-keep">필수 항목에 모두 동의해야 인증을 완료할 수 있습니다</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};
